import React, { useState, useEffect } from 'react';
import {
  FiInfo,
  FiUser,
  FiMapPin,
  FiAlertCircle,
  FiActivity,
  FiClock,
  FiEdit,
  FiChevronLeft,
  FiChevronRight,
  FiMonitor,
  FiLogOut,
  FiCheck,
  FiX,
  FiBriefcase,
  FiLayers,
  FiFileText,
  FiAward,
  FiPlus,
  FiTrash2,
  FiUpload,
  FiEye,
  FiSave,
  FiUsers,
  FiCreditCard,
  FiMail,
  FiPhone,
} from 'react-icons/fi';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../libs/axios';
import { formatDate } from '../activities/edit';
import { useAuth } from '../../provider/auth.provider';
import { getAssociationDisplayLabel } from '../../utils/association.utils';
import { SelectWithSearch2, SelectOption } from '../../utils/select-with-search';
import toast from 'react-hot-toast';
import DragDropFileInput from '../../components/DragDropFileInput';

interface Role {
  id: number;
  name: string;
}

interface State {
  id: number;
  state_name: string;
}

interface District {
  id: number;
  name: string;
}

interface Designation {
  id: number;
  name: string;
}

interface Department {
  id: number;
  name: string;
}

interface UserProject {
  id: number;
  user_id: number;
  project_id: number;
  created_at: string;
  updated_at: string;
  project: {
    project_name: string;
    project_short_name: string;
    project_description: string;
  };
}

interface User {
  id: number;
  email: string;
  employee_code: string;
  username: string;
  email_verified: boolean;
  phone_number: string;
  country_code: string;
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
  gender: string | null;
  profile_picture: string | null;
  address: string | null;
  city: string | null;
  state_circle_id: number;
  district_id: number;
  project_manager_id: number;
  designation_id: number;
  department_id: number | null;
  country: string | null;
  pincode: string | null;
  role_id: number;
  association_type: string;
  status: string;
  employment_status: string;
  reporting_latitude: string | null;
  reporting_longitude: string | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  role: Role;
  state: State | null;
  district: District | null;
  user_projects: UserProject[];
  designation: Designation | null;
  department: Department | null;
}

interface LogEntry {
  id: string;
  user_id: number;
  entity: string;
  entity_id: number | null;
  action: string;
  message: string;
  prev_data: any;
  modified_data: any;
  meta_data: {
    url: string;
    method: string;
    timestamp: string;
    ip_address: string;
    user_agent: string;
  };
  created_at: string;
  updated_at: string;
}

interface Device {
  id: number;
  device_id: string;
  device_name: string;
  device_type: string;
  browser: string;
  ip_address: string | null;
  last_login: string | null;
  token_expires_at: string | null;
  is_verified: boolean;
  status?: string | null;
  is_suspended?: boolean;
  suspended_at?: string | null;
  created_at: string;
  is_current: boolean;
}

interface LogsResponse {
  logs: LogEntry[];
  pagination: {
    offset: number;
    limit: number;
    page: number;
    total_pages: number;
    total_count: number;
    has_next_page: boolean;
    has_prev_page: boolean;
    next_page: number | null;
    prev_page: number | null;
  };
}

interface ReverseGeocode {
  formatted_address?: string;
  locality?: string;
  administrative_area?: string;
  country?: string;
  postal_code?: string;
}

// User Details Enums
enum MaritalStatus {
  SINGLE = 'single',
  MARRIED = 'married',
  DIVORCED = 'divorced',
  WIDOWED = 'widowed',
}

enum PortalAccessStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
}

enum BloodGroup {
  A_POSITIVE = 'A+',
  A_NEGATIVE = 'A-',
  B_POSITIVE = 'B+',
  B_NEGATIVE = 'B-',
  AB_POSITIVE = 'AB+',
  AB_NEGATIVE = 'AB-',
  O_POSITIVE = 'O+',
  O_NEGATIVE = 'O-',
}

interface UserDetailsData {
  id?: number;
  user_id?: number;
  date_of_joining?: string;
  portal_access_status?: PortalAccessStatus;
  date_of_leaving?: string;
  alternate_mobile_number?: string;
  address_permanent?: string;
  marital_status?: MaritalStatus;
  education?: string;
  email_company?: string;
  aadhar_card_number?: string;
  pan_card_number?: string;
  licence_number?: string;
  emergency_mobile_number?: string;
  blood_group?: BloodGroup;
  bank_name?: string;
  bank_account_number?: string;
  ifsc_code?: string;
  esi_number?: string;
  esi_tic_attachment?: string;
  pf_number?: string;
  referred_by?: string;
  father_name?: string;
  father_aadhar_card_number?: string;
  mother_name?: string;
  mother_aadhar_card_number?: string;
  spouse_name?: string;
  spouse_aadhar_card_number?: string;
  child_name_1?: string;
  child_name_2?: string;
  family_picture?: string;
  bank_passbook?: string;
  marksheet?: string;
  aadhar_card_document?: string;
  pan_card_document?: string;
  driving_licence_document?: string;
  employee_id_card_temporary?: string;
  employee_id_card_permanent?: string;
  employee_id_card_permanent_back?: string;
  payment_qr_code?: string;
  created_at?: string;
  updated_at?: string;
}

interface UserCertification {
  id: number;
  user_id: number;
  training_certification_id: number;
  created_at?: string;
  training_certification?: {
    id: number;
    certificate_number: string;
    training_date: string;
    expiry_date: string;
    training_amount?: number;
    card_front_url?: string;
    card_back_url?: string;
    training_type?: {
      id: number;
      training_name: string;
    };
    training_institute?: {
      id: number;
      institute_name: string;
    };
  };
}

interface TrainingCertificationOption {
  id: number;
  certificate_number: string;
  training_date: string;
  expiry_date: string;
  training_type?: {
    training_name: string;
  };
  training_institute?: {
    institute_name: string;
  };
}

const UserView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEditBankInfo = hasPermission('user_details', 'edit_bank');
  const canViewContact = hasPermission('Team', 'view_contact');
  const canViewQrCode = hasPermission('user_details', 'view_qr');
  const canCreateQrCode = hasPermission('user_details', 'create_qr');
  const canEditQrCode = hasPermission('user_details', 'edit_qr');
  const canEditProjects = hasPermission('Team', 'edit_projects');
  const [user, setUser] = useState<User | null>(null);
  const [logs, setLogs] = useState<LogsResponse | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [logsLoading, setLogsLoading] = useState<boolean>(false);
  const [devicesLoading, setDevicesLoading] = useState<boolean>(false);
  const [logoutLoading, setLogoutLoading] = useState<string | null>(null);
  const [deviceActionLoading, setDeviceActionLoading] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('details');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(10);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState<string | null>(null);
  const [locationAddress, setLocationAddress] = useState<string | null>(null);
  const [locationLoading, setLocationLoading] = useState<boolean>(false);
  const [reverseGeocode, setReverseGeocode] = useState<ReverseGeocode | null>(null);
  const [geocodeLoading, setGeocodeLoading] = useState<boolean>(false);

  // Extended Details State
  const [userDetails, setUserDetails] = useState<UserDetailsData | null>(null);
  const [userDetailsLoading, setUserDetailsLoading] = useState<boolean>(false);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [detailsFormData, setDetailsFormData] = useState<UserDetailsData>({});
  const [detailsSaving, setDetailsSaving] = useState<boolean>(false);
  const [detailsFiles, setDetailsFiles] = useState<Record<string, File | null>>({});

  // User Certifications State
  const [userCertifications, setUserCertifications] = useState<UserCertification[]>([]);
  const [certificationsLoading, setCertificationsLoading] = useState<boolean>(false);
  const [showAddCertificationModal, setShowAddCertificationModal] = useState<boolean>(false);
  const [selectedCertification, setSelectedCertification] = useState<SelectOption | null>(null);
  const [addingCertification, setAddingCertification] = useState<boolean>(false);

  // Projects tab state
  const [selectedProjectToAdd, setSelectedProjectToAdd] = useState<SelectOption | null>(null);
  const [projectsSaving, setProjectsSaving] = useState<boolean>(false);

  const tabs = [
    { id: 'details', label: 'User Details', icon: FiInfo },
    ...(canViewContact ? [{ id: 'contact', label: 'Contact Details', icon: FiMail }] : []),
    { id: 'projects', label: 'Projects', icon: FiLayers },
    { id: 'certifications', label: 'Certifications', icon: FiAward },
    { id: 'devices', label: 'Devices', icon: FiMonitor },
    { id: 'logs', label: 'Activity Logs', icon: FiActivity },
  ];

  useEffect(() => {
    fetchUserData();
  }, [id]);

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchUserLogs(currentPage);
    } else if (activeTab === 'devices') {
      fetchUserDevices();
    } else if (activeTab === 'certifications') {
      fetchUserCertifications();
    } else if (activeTab === 'contact') {
      if (!userDetails && !userDetailsLoading) fetchUserDetails();
    }
  }, [activeTab, currentPage]);

  useEffect(() => {
    if (user?.reporting_latitude && user?.reporting_longitude) {
      fetchReverseGeocode(
        parseFloat(user.reporting_latitude),
        parseFloat(user.reporting_longitude)
      );
    }
  }, [user?.reporting_latitude, user?.reporting_longitude]);

  useEffect(() => {
    if (user?.reporting_latitude && user?.reporting_longitude) {
      fetchReverseGeocode(Number(user.reporting_latitude), Number(user.reporting_longitude));
    }
  }, [user]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/users/${id}`);
      setUser(response.data.data);
    } catch (error) {
      console.error('Error fetching user:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReverseGeocode = async (lat: number, lng: number) => {
    try {
      setLocationLoading(true);
      const response = await axiosInstance.post('/maps/reverse-geocode', { lat, lng });
      if (response.data.success) {
        setLocationAddress(response.data.data.formatted_address || response.data.data.address);
      }
    } catch (error) {
      console.error('Error fetching reverse geocode:', error);
    } finally {
      setLocationLoading(false);
    }
  };

  const fetchUserLogs = async (page: number) => {
    try {
      setLogsLoading(true);
      const offset = (page - 1) * itemsPerPage;
      const response = await axiosInstance.get(`/logs/${id}/user`, {
        params: {
          limit: itemsPerPage,
          offset: offset,
        },
      });
      setLogs(response.data.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const fetchUserDevices = async () => {
    try {
      setDevicesLoading(true);
      const response = await axiosInstance.get(`/users/${id}/devices`);
      setDevices(response.data.data.devices);
    } catch (error) {
      console.error('Error fetching devices:', error);
    } finally {
      setDevicesLoading(false);
    }
  };

  const fetchUserDetails = async () => {
    try {
      setUserDetailsLoading(true);
      const response = await axiosInstance.get(`/user-details/${id}/details`);
      if (response.data.success) {
        setUserDetails(response.data.data.user_details);
      }
    } catch (error: any) {
      if (error.response?.status !== 404) {
        console.error('Error fetching user details:', error);
      }
      setUserDetails(null);
    } finally {
      setUserDetailsLoading(false);
    }
  };

  const fetchUserCertifications = async () => {
    try {
      setCertificationsLoading(true);
      const response = await axiosInstance.get(`/user-details/${id}/certifications`);
      if (response.data.success) {
        setUserCertifications(response.data.data.certifications || []);
      }
    } catch (error) {
      console.error('Error fetching certifications:', error);
      setUserCertifications([]);
    } finally {
      setCertificationsLoading(false);
    }
  };

  const handleSaveUserDetails = async () => {
    try {
      setDetailsSaving(true);
      const formData = new FormData();

      // Append text fields
      Object.entries(detailsFormData).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          formData.append(key, String(value));
        }
      });

      // Append file fields
      Object.entries(detailsFiles).forEach(([key, file]) => {
        if (file) {
          formData.append(key, file);
        }
      });

      await axiosInstance.post(`/user-details/${id}/details`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success(userDetails ? 'Details updated successfully' : 'Details added successfully');
      setShowDetailsModal(false);
      setDetailsFiles({});
      fetchUserDetails();
    } catch (error: any) {
      console.error('Error saving user details:', error);
      toast.error(error.response?.data?.message || 'Failed to save details');
    } finally {
      setDetailsSaving(false);
    }
  };

  const handleAddCertification = async () => {
    if (!selectedCertification) {
      toast.error('Please select a certification');
      return;
    }

    try {
      setAddingCertification(true);
      await axiosInstance.post(`/user-details/${id}/certifications`, {
        training_certification_id: selectedCertification.id,
      });

      toast.success('Certification linked successfully');
      setShowAddCertificationModal(false);
      setSelectedCertification(null);
      fetchUserCertifications();
    } catch (error: any) {
      console.error('Error adding certification:', error);
      toast.error(error.response?.data?.message || 'Failed to link certification');
    } finally {
      setAddingCertification(false);
    }
  };

  const handleUnlinkCertification = async (certificationId: number) => {
    try {
      await axiosInstance.delete(`/user-details/${id}/certifications/${certificationId}`);
      toast.success('Certification unlinked successfully');
      fetchUserCertifications();
    } catch (error: any) {
      console.error('Error unlinking certification:', error);
      toast.error(error.response?.data?.message || 'Failed to unlink certification');
    }
  };

  const openEditDetailsModal = () => {
    setDetailsFormData(userDetails || {});
    setDetailsFiles({});
    setShowDetailsModal(true);
  };

  const handleDetailsInputChange = (field: keyof UserDetailsData, value: any) => {
    setDetailsFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (field: string, file: File | null) => {
    setDetailsFiles((prev) => ({ ...prev, [field]: file }));
  };

  const handleLogoutDevice = async (deviceId: string) => {
    try {
      setLogoutLoading(deviceId);
      await axiosInstance.post(`/users/logout-device/${deviceId}`);
      // Refresh devices list after logout
      await fetchUserDevices();
      setShowLogoutConfirm(null);
    } catch (error) {
      console.error('Error logging out device:', error);
    } finally {
      setLogoutLoading(null);
    }
  };

  const handleApproveDevice = async (deviceId: number) => {
    try {
      setDeviceActionLoading(`approve-${deviceId}`);
      await axiosInstance.put(`/auth/admin/devices/${deviceId}/approve`);
      await fetchUserDevices();
    } catch (error) {
      console.error('Error approving device:', error);
    } finally {
      setDeviceActionLoading(null);
    }
  };

  const handleSuspendDevice = async (deviceId: number) => {
    try {
      setDeviceActionLoading(`suspend-${deviceId}`);
      await axiosInstance.put(`/auth/admin/devices/${deviceId}/suspend`);
      await fetchUserDevices();
    } catch (error) {
      console.error('Error suspending device:', error);
    } finally {
      setDeviceActionLoading(null);
    }
  };

  const formatDeviceDateTime = (dateString?: string | null, emptyLabel: string = 'N/A') => {
    if (!dateString) {
      return emptyLabel;
    }

    const parsedDate = new Date(dateString);
    if (Number.isNaN(parsedDate.getTime())) {
      return emptyLabel;
    }

    return parsedDate.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRawDeviceStatus = (device: Device) => String(device.status || '').trim().toLowerCase();

  const getDeviceStatus = (device: Device) => {
    const rawStatus = getRawDeviceStatus(device);

    if (device.is_suspended === true || Boolean(device.suspended_at) || rawStatus === 'suspended') {
      return 'suspended';
    }

    if (rawStatus === 'requested' || rawStatus === 'pending') {
      return 'requested';
    }

    if (device.is_verified) {
      return 'approved';
    }

    return rawStatus || 'requested';
  };

  const isDeviceApproved = (device: Device) => {
    return getDeviceStatus(device) === 'approved';
  };

  const isDeviceSuspended = (device: Device) => {
    return getDeviceStatus(device) === 'suspended';
  };

  const hasActiveDeviceSession = (device: Device) => {
    return getRawDeviceStatus(device) === 'active' && Boolean(device.token_expires_at);
  };

  const getDeviceStatusBadge = (device: Device) => {
    const status = getDeviceStatus(device);
    const config: Record<string, { bgColor: string; textColor: string; label: string }> = {
      approved: { bgColor: 'bg-green-100', textColor: 'text-green-800', label: 'Approved' },
      requested: { bgColor: 'bg-yellow-100', textColor: 'text-yellow-800', label: 'Requested' },
      pending: { bgColor: 'bg-yellow-100', textColor: 'text-yellow-800', label: 'Pending' },
      suspended: { bgColor: 'bg-red-100', textColor: 'text-red-800', label: 'Suspended' },
      inactive: { bgColor: 'bg-gray-100', textColor: 'text-gray-800', label: 'Inactive' },
    };

    const statusConfig = config[status] || {
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-800',
      label: status.charAt(0).toUpperCase() + status.slice(1),
    };

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusConfig.bgColor} ${statusConfig.textColor}`}
      >
        {statusConfig.label}
      </span>
    );
  };

  const getFullName = (user?: User | null) => {
    if (!user) return 'N/A';
    return `${user.first_name} ${user.last_name}`;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { bgColor: string; textColor: string; label: string }> = {
      verified: { bgColor: 'bg-green-100', textColor: 'text-green-800', label: 'Verified' },
      pending: { bgColor: 'bg-yellow-100', textColor: 'text-yellow-800', label: 'Pending' },
      suspended: { bgColor: 'bg-red-100', textColor: 'text-red-800', label: 'Suspended' },
      inactive: { bgColor: 'bg-gray-100', textColor: 'text-gray-800', label: 'Inactive' },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bgColor} ${config.textColor}`}>
        {config.label}
      </span>
    );
  };

  const getAssociationBadge = (type: string) => {
    return type === 'inhouse' ? (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
        {getAssociationDisplayLabel('inhouse')}
      </span>
    ) : (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
        {getAssociationDisplayLabel('vendor')}
      </span>
    );
  };

  const getEmploymentStatusBadge = (status: string) => {
    const config: Record<string, { bgColor: string; textColor: string; label: string }> = {
      working: { bgColor: 'bg-green-100', textColor: 'text-green-800', label: 'Working' },
      not_working: { bgColor: 'bg-yellow-100', textColor: 'text-yellow-800', label: 'Not Working' },
      left: { bgColor: 'bg-red-100', textColor: 'text-red-800', label: 'Left' },
    };

    const s = config[status] || { bgColor: 'bg-gray-100', textColor: 'text-gray-800', label: status };

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${s.bgColor} ${s.textColor}`}>
        {s.label}
      </span>
    );
  };

  const getActionBadge = (action: string) => {
    const actionConfig: Record<string, { bgColor: string; textColor: string }> = {
      login: { bgColor: 'bg-blue-100', textColor: 'text-blue-800' },
      logout: { bgColor: 'bg-gray-100', textColor: 'text-gray-800' },
      create: { bgColor: 'bg-green-100', textColor: 'text-green-800' },
      update: { bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' },
      delete: { bgColor: 'bg-red-100', textColor: 'text-red-800' },
      approve: { bgColor: 'bg-green-100', textColor: 'text-green-800' },
      reject: { bgColor: 'bg-red-100', textColor: 'text-red-800' },
    };

    const config = actionConfig[action] || { bgColor: 'bg-gray-100', textColor: 'text-gray-800' };

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bgColor} ${config.textColor}`}>
        {action}
      </span>
    );
  };

  const getEntityBadge = (entity: string) => {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
        {entity}
      </span>
    );
  };

  const renderContactTab = () => (
    <div className="space-y-4">
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Contact Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded border border-gray-100">
            <FiMail className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Primary Email</p>
              <p className="text-sm text-gray-900">{user?.email || '—'}</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded border border-gray-100">
            <FiPhone className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Phone Number</p>
              <p className="text-sm text-gray-900">{user?.phone_number || '—'}</p>
            </div>
          </div>
          {userDetailsLoading ? (
            <div className="md:col-span-2 flex items-center gap-2 text-xs text-gray-500 py-2">
              <div className="w-3 h-3 border-2 border-blue-800 border-t-transparent rounded-full animate-spin" />
              Loading additional contact details...
            </div>
          ) : (
            <>
              {userDetails?.email_company && (
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded border border-gray-100">
                  <FiMail className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Company Email</p>
                    <p className="text-sm text-gray-900">{userDetails.email_company}</p>
                  </div>
                </div>
              )}
              {userDetails?.alternate_mobile_number && (
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded border border-gray-100">
                  <FiPhone className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Alternate Mobile</p>
                    <p className="text-sm text-gray-900">{userDetails.alternate_mobile_number}</p>
                  </div>
                </div>
              )}
              {userDetails?.emergency_mobile_number && (
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded border border-gray-100">
                  <FiPhone className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Emergency Contact</p>
                    <p className="text-sm text-gray-900">{userDetails.emergency_mobile_number}</p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  // ── Projects tab (item 7) ─────────────────────────────────────────────────
  const saveUserProjects = async (newProjectIds: number[]) => {
    if (!user) return;
    setProjectsSaving(true);
    try {
      const res = await axiosInstance.put(`/users/${user.id}/projects`, { project_ids: newProjectIds });
      const updated: UserProject[] = res.data?.data?.user_projects ?? [];
      setUser({ ...user, user_projects: updated });
      toast.success('Projects updated');
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? 'Failed to update projects');
    } finally {
      setProjectsSaving(false);
    }
  };

  const handleAddProject = async () => {
    if (!user || !selectedProjectToAdd) return;
    const currentIds = (user.user_projects || []).map((up) => up.project_id);
    const addId = Number(selectedProjectToAdd.value);
    if (currentIds.includes(addId)) {
      toast.error('Project already assigned');
      return;
    }
    await saveUserProjects([...currentIds, addId]);
    setSelectedProjectToAdd(null);
  };

  const handleRemoveProject = async (projectId: number) => {
    if (!user) return;
    const currentIds = (user.user_projects || []).map((up) => up.project_id);
    await saveUserProjects(currentIds.filter((id) => id !== projectId));
  };

  const renderProjectsTab = () => (
    <div className="space-y-4">
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <FiLayers className="w-4 h-4" />
            Assigned Projects
            <span className="text-xs font-normal text-gray-500">({user?.user_projects?.length || 0})</span>
          </h3>
          {!canEditProjects && (
            <span className="text-xs text-gray-400 italic">View only</span>
          )}
        </div>

        {canEditProjects && (
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <div className="flex-1">
              <SelectWithSearch2
                placeholder="Search projects to add..."
                value={selectedProjectToAdd}
                onChange={(option) => setSelectedProjectToAdd(option)}
                apiEndpoint="/projects/list"
                dataKey="projects"
                extraParams={{ limit: 10 }}
                mapOption={(p: any) => ({
                  id: p.id,
                  value: p.id,
                  label: p.project_name + (p.project_short_name ? ` (${p.project_short_name})` : ''),
                })}
              />
            </div>
            <button
              onClick={handleAddProject}
              disabled={!selectedProjectToAdd || projectsSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {projectsSaving ? 'Saving...' : 'Add Project'}
            </button>
          </div>
        )}

        {user?.user_projects && user.user_projects.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {user.user_projects.map((userProject) => (
              <div
                key={userProject.project_id}
                className="border border-gray-200 rounded-lg p-3 flex items-start justify-between hover:border-blue-300 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-gray-900 truncate">
                    {userProject.project?.project_name}
                  </h4>
                  {userProject.project?.project_short_name && (
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{userProject.project.project_short_name}</p>
                  )}
                </div>
                {canEditProjects && (
                  <button
                    onClick={() => handleRemoveProject(userProject.project_id)}
                    disabled={projectsSaving}
                    className="ml-2 p-1 text-gray-400 hover:text-red-600 disabled:opacity-50"
                    title="Remove project"
                  >
                    <FiX className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 py-6 text-center">No projects assigned</p>
        )}
      </div>
    </div>
  );

  const renderDetailsTab = () => (
    <div className="space-y-4">
      {/* Basic Information */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Basic Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-gray-500">Full Name</label>
            <p className="text-sm text-gray-900 mt-1 font-semibold">{getFullName(user)}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Username</label>
            <p className="text-sm text-gray-900 mt-1">{user?.username}</p>
          </div>
          {user?.employee_code && (
            <div>
              <label className="text-xs font-medium text-gray-500">Employee Code</label>
              <p className="text-sm text-gray-900 mt-1 font-mono">{user.employee_code}</p>
            </div>
          )}
          {/* <div>
            <label className="text-xs font-medium text-gray-500">Email</label>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-sm text-gray-900">{user?.email}</p>
              {user?.email_verified !== undefined && getVerificationBadge(user.email_verified)}
            </div>
          </div> */}
          {/* <div>
            <label className="text-xs font-medium text-gray-500">Phone Number</label>
            <p className="text-sm text-gray-900 mt-1">
              {user?.phone_number ? formatPhoneNumber(user.country_code, user.phone_number) : 'N/A'}
            </p>
          </div> */}
          <div>
            <label className="text-xs font-medium text-gray-500">Role</label>
            <p className="text-sm text-gray-900 mt-1 font-medium">{user?.role.name}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Department</label>
            <p className="text-sm text-gray-900 mt-1 font-medium">{user?.department?.name || 'N/A'}</p>
          </div>
          {user?.designation && (
            <div>
              <label className="text-xs font-medium text-gray-500">Designation</label>
              <p className="text-sm text-gray-900 mt-1 font-medium">{user.designation.name}</p>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-gray-500">Association Type</label>
            <div className="mt-1">
              {user?.association_type ? getAssociationBadge(user.association_type) : 'N/A'}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Employment Status</label>
            <div className="mt-1">
              {user?.employment_status ? getEmploymentStatusBadge(user.employment_status) : 'N/A'}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Status</label>
            <div className="mt-1">{getStatusBadge(user?.status || 'pending')}</div>
          </div>
          {user?.date_of_birth && (
            <div>
              <label className="text-xs font-medium text-gray-500">Date of Birth</label>
              <p className="text-sm text-gray-900 mt-1">{formatDate(user.date_of_birth)}</p>
            </div>
          )}
          {user?.gender && (
            <div>
              <label className="text-xs font-medium text-gray-500">Gender</label>
              <p className="text-sm text-gray-900 mt-1 capitalize">{user.gender}</p>
            </div>
          )}
        </div>
      </div>

      {/* Projects */}
      {user?.user_projects && user.user_projects.length > 0 && (
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FiLayers className="w-4 h-4" />
            Assigned Projects
          </h3>
          <div className="grid grid-cols-3 gap-3">
            {user.user_projects.map((userProject, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-3 hover:border-blue-300 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-900">
                      {userProject.project.project_name}
                    </h4>
                    <p className="text-xs text-gray-600 mt-1">
                      Short Name: <span className="font-medium">{userProject.project.project_short_name}</span>
                    </p>
                    {userProject.project.project_description && (
                      <p className="text-xs text-gray-600 mt-1">
                        {userProject.project.project_description}
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t border-gray-200 flex items-center gap-4 text-xs text-gray-500">
                  <span>Assigned: {formatDate(userProject.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Location Information */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Location Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-gray-500">Reporting State</label>
            <p className="text-sm text-gray-900 mt-1">{user?.state?.state_name || 'N/A'}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Reporting District</label>
            <p className="text-sm text-gray-900 mt-1">{user?.district?.name || 'N/A'}</p>
          </div>
          {user?.city && (
            <div>
              <label className="text-xs font-medium text-gray-500">City</label>
              <p className="text-sm text-gray-900 mt-1">{user.city}</p>
            </div>
          )}
          {user?.pincode && (
            <div>
              <label className="text-xs font-medium text-gray-500">Pincode</label>
              <p className="text-sm text-gray-900 mt-1">{user.pincode}</p>
            </div>
          )}
          {user?.country && (
            <div>
              <label className="text-xs font-medium text-gray-500">Country</label>
              <p className="text-sm text-gray-900 mt-1">{user.country}</p>
            </div>
          )}
          {user?.address && (
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-gray-500">Address</label>
              <p className="text-sm text-gray-900 mt-1 bg-gray-50 p-3 rounded">{user.address}</p>
            </div>
          )}
          {(user?.reporting_latitude || user?.reporting_longitude) && (
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-gray-500">Reporting Location</label>
              <div className="mt-1 space-y-2">
                <p className="text-sm text-gray-900">
                  Coordinates: {user?.reporting_latitude || 'N/A'}, {user?.reporting_longitude || 'N/A'}
                </p>
                {locationLoading ? (
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <div className="w-3 h-3 border-2 border-blue-800 border-t-transparent rounded-full animate-spin"></div>
                    Fetching location address...
                  </div>
                ) : locationAddress ? (
                  <div className="bg-blue-50 p-3 rounded border border-blue-200">
                    <div className="flex items-start gap-2">
                      <FiMapPin className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-blue-900">{locationAddress}</p>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Account Information */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Account Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-gray-500">Created At</label>
            <p className="text-sm text-gray-900 mt-1">{user?.created_at ? formatDate(user.created_at) : 'N/A'}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Last Updated</label>
            <p className="text-sm text-gray-900 mt-1">{user?.updated_at ? formatDate(user.updated_at) : 'N/A'}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDevicesTab = () => {
    if (devicesLoading && devices.length === 0) {
      return (
        <div className="bg-white rounded shadow-sm border border-gray-200 p-8">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-blue-800 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading devices...</p>
          </div>
        </div>
      );
    }

    if (devices.length === 0) {
      return (
        <div className="bg-white rounded shadow-sm border border-gray-200 p-8">
          <div className="text-center">
            <FiMonitor className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Devices Found</h3>
            <p className="text-gray-600">No devices found for this user.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900">Devices</h3>
            <span className="text-sm text-gray-600">
              Total: {devices.length} {devices.length === 1 ? 'device' : 'devices'}
            </span>
          </div>

          <div className="space-y-3">
            {[...devices]
              .sort((a, b) => {
                const aApproved = isDeviceApproved(a) ? 1 : 0;
                const bApproved = isDeviceApproved(b) ? 1 : 0;

                if (aApproved !== bApproved) {
                  return bApproved - aApproved;
                }

                const aCreatedAt = new Date(a.created_at).getTime();
                const bCreatedAt = new Date(b.created_at).getTime();
                return bCreatedAt - aCreatedAt;
              })
              .map((device) => {
                const deviceApproved = isDeviceApproved(device);
                const deviceSuspended = isDeviceSuspended(device);
                const canLogoutDevice = !device.is_current && hasActiveDeviceSession(device);
                const isApproveLoading = deviceActionLoading === `approve-${device.id}`;
                const isSuspendLoading = deviceActionLoading === `suspend-${device.id}`;
                const isDeviceActionLoading = isApproveLoading || isSuspendLoading;

                return (
                  <div
                    key={device.id}
                    className={`border rounded-lg p-4 transition-colors ${
                      device.is_current
                        ? 'border-green-300 bg-green-50'
                        : 'border-gray-200 bg-white hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          device.is_current ? 'bg-green-100' : 'bg-gray-100'
                        }`}>
                          <FiMonitor className={`w-5 h-5 ${
                            device.is_current ? 'text-green-600' : 'text-gray-600'
                          }`} />
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                            {device.device_name || 'Unknown Device'}
                            {device.is_current && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                <FiCheck className="w-3 h-3 mr-1" />
                                Current
                              </span>
                            )}
                          </h4>
                          <p className="text-xs text-gray-600 mt-0.5">{device.device_type || 'Unknown Type'}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap justify-end">
                        {!deviceApproved && (
                          <button
                            onClick={() => handleApproveDevice(device.id)}
                            disabled={isDeviceActionLoading || logoutLoading === device.device_id}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <FiCheck className="w-4 h-4" />
                            {isApproveLoading ? 'Approving...' : 'Approve'}
                          </button>
                        )}
                        {!deviceSuspended && (
                          <button
                            onClick={() => handleSuspendDevice(device.id)}
                            disabled={isDeviceActionLoading || logoutLoading === device.device_id}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded hover:bg-amber-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <FiX className="w-4 h-4" />
                            {isSuspendLoading ? 'Suspending...' : 'Suspend'}
                          </button>
                        )}
                        {canLogoutDevice && (
                          <button
                            onClick={() => setShowLogoutConfirm(device.device_id)}
                            disabled={logoutLoading === device.device_id || isDeviceActionLoading}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <FiLogOut className="w-4 h-4" />
                            {logoutLoading === device.device_id ? 'Logging out...' : 'Logout'}
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
                      <div className="bg-white p-2 rounded border border-gray-200">
                        <span className="font-medium text-gray-700">Browser:</span>{' '}
                        <span className="text-gray-900">{device.browser || 'N/A'}</span>
                      </div>
                      <div className="bg-white p-2 rounded border border-gray-200">
                        <span className="font-medium text-gray-700">IP Address:</span>{' '}
                        <span className="font-mono text-gray-900">{device.ip_address || 'N/A'}</span>
                      </div>
                      <div className="bg-white p-2 rounded border border-gray-200">
                        <span className="font-medium text-gray-700">Last Login:</span>{' '}
                        <span className="text-gray-900">{formatDeviceDateTime(device.last_login)}</span>
                      </div>
                      <div className="bg-white p-2 rounded border border-gray-200">
                        <span className="font-medium text-gray-700">Session Expires:</span>{' '}
                        <span className="text-gray-900">{formatDeviceDateTime(device.token_expires_at, 'Not logged in')}</span>
                      </div>
                      <div className="bg-white p-2 rounded border border-gray-200">
                        <span className="font-medium text-gray-700">Created:</span>{' '}
                        <span className="text-gray-900">{formatDeviceDateTime(device.created_at)}</span>
                      </div>
                      <div className="bg-white p-2 rounded border border-gray-200">
                        <span className="font-medium text-gray-700">Status:</span>{' '}
                        {getDeviceStatusBadge(device)}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    );
  };

  const renderLogsTab = () => {
    if (logsLoading && !logs) {
      return (
        <div className="bg-white rounded shadow-sm border border-gray-200 p-8">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-blue-800 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading activity logs...</p>
          </div>
        </div>
      );
    }

    if (!logs || logs.logs.length === 0) {
      return (
        <div className="bg-white rounded shadow-sm border border-gray-200 p-8">
          <div className="text-center">
            <FiActivity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Activity Logs</h3>
            <p className="text-gray-600">No activity logs found for this user.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900">Activity Timeline</h3>
            <span className="text-sm text-gray-600">
              Total: {logs.pagination.total_count} {logs.pagination.total_count === 1 ? 'entry' : 'entries'}
            </span>
          </div>

          <div className="space-y-3">
            {logs.logs.map((log, index) => (
              <div
                key={log.id}
                className={`border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors ${
                  index === 0 ? 'bg-blue-50' : 'bg-white'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {getEntityBadge(log.entity)}
                    {getActionBadge(log.action)}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <FiClock className="w-3 h-3" />
                    {formatDate(log.created_at)}
                  </div>
                </div>

                <p className="text-sm text-gray-900 mb-3 font-medium">{log.message}</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div className="bg-gray-50 p-2 rounded">
                    <span className="font-medium text-gray-700">Method:</span>{' '}
                    <span className={`font-mono ${
                      log.meta_data.method === 'POST' ? 'text-green-700' :
                      log.meta_data.method === 'PUT' ? 'text-yellow-700' :
                      log.meta_data.method === 'DELETE' ? 'text-red-700' :
                      'text-blue-700'
                    }`}>
                      {log.meta_data.method}
                    </span>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <span className="font-medium text-gray-700">IP:</span>{' '}
                    <span className="font-mono text-gray-900">{log.meta_data.ip_address}</span>
                  </div>
                  <div className="bg-gray-50 p-2 rounded sm:col-span-2">
                    <span className="font-medium text-gray-700">URL:</span>{' '}
                    <span className="font-mono text-gray-900 break-all">{log.meta_data.url}</span>
                  </div>
                </div>

                {(log.prev_data || log.modified_data) && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <details className="text-xs">
                      <summary className="cursor-pointer font-medium text-gray-700 hover:text-gray-900">
                        View Data Changes
                      </summary>
                      <div className="mt-2 space-y-2">
                        {log.prev_data && (
                          <div>
                            <p className="font-medium text-gray-700 mb-1">Previous Data:</p>
                            <pre className="bg-gray-100 p-2 rounded overflow-x-auto text-xs">
                              {JSON.stringify(log.prev_data, null, 2)}
                            </pre>
                          </div>
                        )}
                        {log.modified_data && (
                          <div>
                            <p className="font-medium text-gray-700 mb-1">Modified Data:</p>
                            <pre className="bg-gray-100 p-2 rounded overflow-x-auto text-xs">
                              {JSON.stringify(log.modified_data, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </details>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {logs.pagination.total_pages > 1 && (
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Page {logs.pagination.page} of {logs.pagination.total_pages}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={!logs.pagination.has_prev_page || logsLoading}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronLeft className="w-4 h-4" />
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={!logs.pagination.has_next_page || logsLoading}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <FiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const getPortalStatusBadge = (status?: PortalAccessStatus) => {
    const config: Record<string, { bg: string; text: string; label: string }> = {
      active: { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
      inactive: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Inactive' },
      suspended: { bg: 'bg-red-100', text: 'text-red-800', label: 'Suspended' },
    };
    const s = config[status || 'inactive'] || config.inactive;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${s.bg} ${s.text}`}>
        {s.label}
      </span>
    );
  };

  const getMaritalStatusLabel = (status?: MaritalStatus) => {
    const labels: Record<string, string> = {
      single: 'Single',
      married: 'Married',
      divorced: 'Divorced',
      widowed: 'Widowed',
    };
    return labels[status || ''] || '-';
  };

  const renderDocumentLink = (url?: string, label?: string) => {
    if (!url) return <span className="text-gray-400">Not uploaded</span>;
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm"
      >
        <FiEye className="w-4 h-4" />
        View {label || 'Document'}
      </a>
    );
  };

  const renderExtendedDetailsTab = () => {
    if (userDetailsLoading) {
      return (
        <div className="bg-white rounded shadow-sm border border-gray-200 p-8">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-blue-800 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading extended details...</p>
          </div>
        </div>
      );
    }

    if (!userDetails) {
      return (
        <div className="bg-white rounded shadow-sm border border-gray-200 p-8">
          <div className="text-center">
            <FiFileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Extended Details</h3>
            <p className="text-gray-600 mb-4">Extended details have not been added for this user.</p>
            <button
              onClick={openEditDetailsModal}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-700 transition-colors"
            >
              <FiPlus className="w-4 h-4" />
              Add Details
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Header with Edit Button */}
        <div className="flex justify-end">
          <button
            onClick={openEditDetailsModal}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-700 transition-colors text-sm"
          >
            <FiEdit className="w-4 h-4" />
            Edit Details
          </button>
        </div>

        {/* Employment Information */}
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FiBriefcase className="w-4 h-4" />
            Employment Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500">Date of Joining</label>
              <p className="text-sm text-gray-900 mt-1">{userDetails.date_of_joining ? formatDate(userDetails.date_of_joining) : '-'}</p>
            </div>
            {/* <div>
              <label className="text-xs font-medium text-gray-500">Portal Access Status</label>
              <div className="mt-1">{getPortalStatusBadge(userDetails.portal_access_status)}</div>
            </div> */}
            <div>
              <label className="text-xs font-medium text-gray-500">Date of Leaving</label>
              <p className="text-sm text-gray-900 mt-1">{userDetails.date_of_leaving ? formatDate(userDetails.date_of_leaving) : '-'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Company Email</label>
              <p className="text-sm text-gray-900 mt-1">{userDetails.email_company || '-'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Referred By</label>
              <p className="text-sm text-gray-900 mt-1">{userDetails.referred_by || '-'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">ESI Number</label>
              <p className="text-sm text-gray-900 mt-1 font-mono">{userDetails.esi_number || '-'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">PF Number</label>
              <p className="text-sm text-gray-900 mt-1 font-mono">{userDetails.pf_number || '-'}</p>
            </div>
          </div>
        </div>

        {/* Personal Information */}
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FiUser className="w-4 h-4" />
            Personal Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500">Alternate Mobile</label>
              <p className="text-sm text-gray-900 mt-1">{userDetails.alternate_mobile_number || '-'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Emergency Contact</label>
              <p className="text-sm text-gray-900 mt-1">{userDetails.emergency_mobile_number || '-'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Blood Group</label>
              <p className="text-sm text-gray-900 mt-1">{userDetails.blood_group || '-'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Marital Status</label>
              <p className="text-sm text-gray-900 mt-1">{getMaritalStatusLabel(userDetails.marital_status)}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Education</label>
              <p className="text-sm text-gray-900 mt-1">{userDetails.education || '-'}</p>
            </div>
            <div className="md:col-span-2 lg:col-span-3">
              <label className="text-xs font-medium text-gray-500">Permanent Address</label>
              <p className="text-sm text-gray-900 mt-1 bg-gray-50 p-3 rounded">{userDetails.address_permanent || '-'}</p>
            </div>
          </div>
        </div>

        {/* Identity Documents */}
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FiCreditCard className="w-4 h-4" />
            Identity Documents
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500">Aadhar Card Number</label>
              <p className="text-sm text-gray-900 mt-1 font-mono">{userDetails.aadhar_card_number || '-'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Driving Licence</label>
              <p className="text-sm text-gray-900 mt-1 font-mono">{userDetails.licence_number || '-'}</p>
            </div>
          </div>
        </div>

        {/* Bank & Statutory Information */}
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FiCreditCard className="w-4 h-4" />
            Bank & Statutory Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500">PAN Card Number</label>
              <p className="text-sm text-gray-900 mt-1 font-mono">{userDetails.pan_card_number || '-'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Bank Name</label>
              <p className="text-sm text-gray-900 mt-1">{userDetails.bank_name || '-'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Account Number</label>
              <p className="text-sm text-gray-900 mt-1 font-mono">{userDetails.bank_account_number || '-'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">IFSC Code</label>
              <p className="text-sm text-gray-900 mt-1 font-mono">{userDetails.ifsc_code || '-'}</p>
            </div>
            {canViewQrCode && userDetails.payment_qr_code && (
              <div className="md:col-span-2 lg:col-span-3">
                <label className="text-xs font-medium text-gray-500">Payment QR Code</label>
                <div className="mt-2">
                  <img
                    src={userDetails.payment_qr_code}
                    alt="Payment QR Code"
                    className="w-32 h-32 object-contain border border-gray-200 rounded"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Family Information */}
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FiUsers className="w-4 h-4" />
            Family Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 p-3 rounded">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Father</h4>
              <div className="space-y-1">
                <p className="text-sm text-gray-900">Name: {userDetails.father_name || '-'}</p>
                <p className="text-sm text-gray-600">Aadhar: {userDetails.father_aadhar_card_number || '-'}</p>
              </div>
            </div>
            <div className="bg-gray-50 p-3 rounded">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Mother</h4>
              <div className="space-y-1">
                <p className="text-sm text-gray-900">Name: {userDetails.mother_name || '-'}</p>
                <p className="text-sm text-gray-600">Aadhar: {userDetails.mother_aadhar_card_number || '-'}</p>
              </div>
            </div>
            {(userDetails.spouse_name || userDetails.marital_status === MaritalStatus.MARRIED) && (
              <div className="bg-gray-50 p-3 rounded">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Spouse</h4>
                <div className="space-y-1">
                  <p className="text-sm text-gray-900">Name: {userDetails.spouse_name || '-'}</p>
                  <p className="text-sm text-gray-600">Aadhar: {userDetails.spouse_aadhar_card_number || '-'}</p>
                </div>
              </div>
            )}
            {(userDetails.child_name_1 || userDetails.child_name_2) && (
              <div className="bg-gray-50 p-3 rounded">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Children</h4>
                <div className="space-y-1">
                  {userDetails.child_name_1 && <p className="text-sm text-gray-900">1. {userDetails.child_name_1}</p>}
                  {userDetails.child_name_2 && <p className="text-sm text-gray-900">2. {userDetails.child_name_2}</p>}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Uploaded Documents */}
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FiFileText className="w-4 h-4" />
            Uploaded Documents
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="border border-gray-200 rounded p-3">
              <label className="text-xs font-medium text-gray-500 block mb-2">Aadhar Card</label>
              {renderDocumentLink(userDetails.aadhar_card_document)}
            </div>
            <div className="border border-gray-200 rounded p-3">
              <label className="text-xs font-medium text-gray-500 block mb-2">Driving Licence</label>
              {renderDocumentLink(userDetails.driving_licence_document)}
            </div>
            <div className="border border-gray-200 rounded p-3">
              <label className="text-xs font-medium text-gray-500 block mb-2">PAN Card</label>
              {renderDocumentLink(userDetails.pan_card_document)}
            </div>
            <div className="border border-gray-200 rounded p-3">
              <label className="text-xs font-medium text-gray-500 block mb-2">Bank Passbook</label>
              {renderDocumentLink(userDetails.bank_passbook)}
            </div>
            <div className="border border-gray-200 rounded p-3">
              <label className="text-xs font-medium text-gray-500 block mb-2">Marksheet</label>
              {renderDocumentLink(userDetails.marksheet)}
            </div>
            <div className="border border-gray-200 rounded p-3">
              <label className="text-xs font-medium text-gray-500 block mb-2">ESI TIC Attachment</label>
              {renderDocumentLink(userDetails.esi_tic_attachment)}
            </div>
            <div className="border border-gray-200 rounded p-3">
              <label className="text-xs font-medium text-gray-500 block mb-2">Family Picture</label>
              {renderDocumentLink(userDetails.family_picture)}
            </div>
            <div className="border border-gray-200 rounded p-3">
              <label className="text-xs font-medium text-gray-500 block mb-2">Temporary ID Card</label>
              {renderDocumentLink(userDetails.employee_id_card_temporary)}
            </div>
              <div className="border border-gray-200 rounded p-3">
                <label className="text-xs font-medium text-gray-500 block mb-2">Permanent ID Card</label>
                {renderDocumentLink(userDetails.employee_id_card_permanent)}
              </div>
              <div className="border border-gray-200 rounded p-3">
                <label className="text-xs font-medium text-gray-500 block mb-2">Permanent ID Card Back</label>
                {renderDocumentLink(userDetails.employee_id_card_permanent_back)}
              </div>
            </div>
          </div>
        </div>
    );
  };

  const isExpiringSoon = (expiryDate?: string) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const now = new Date();
    const diffDays = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays > 0;
  };

  const isExpired = (expiryDate?: string) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const renderCertificationsTab = () => {
    if (certificationsLoading) {
      return (
        <div className="bg-white rounded shadow-sm border border-gray-200 p-8">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-blue-800 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading certifications...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Header with Add Button */}
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h3 className="text-base font-semibold text-gray-900">User Certifications</h3>
              <p className="text-sm text-gray-600 mt-1">
                {userCertifications.length} {userCertifications.length === 1 ? 'certification' : 'certifications'} linked
              </p>
            </div>
            <button
              onClick={() => setShowAddCertificationModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-700 transition-colors text-sm"
            >
              <FiPlus className="w-4 h-4" />
              Link Certification
            </button>
          </div>
        </div>

        {/* Certifications List */}
        {userCertifications.length === 0 ? (
          <div className="bg-white rounded shadow-sm border border-gray-200 p-8">
            <div className="text-center">
              <FiAward className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Certifications</h3>
              <p className="text-gray-600">No certifications have been linked to this user.</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {userCertifications.map((cert) => {
              const certification = cert.training_certification;
              const expired = isExpired(certification?.expiry_date);
              const expiringSoon = isExpiringSoon(certification?.expiry_date);

              return (
                <div
                  key={cert.id}
                  className={`bg-white rounded shadow-sm border p-4 ${
                    expired ? 'border-red-200 bg-red-50' : expiringSoon ? 'border-orange-200 bg-orange-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <FiAward className={`w-5 h-5 ${expired ? 'text-red-500' : expiringSoon ? 'text-orange-500' : 'text-blue-600'}`} />
                      <h4 className="text-sm font-semibold text-gray-900">
                        {certification?.training_type?.training_name || 'Unknown Training'}
                      </h4>
                    </div>
                    <button
                      onClick={() => handleUnlinkCertification(certification?.id || 0)}
                      className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                      title="Unlink Certification"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Certificate No:</span>
                      <span className="font-mono text-gray-900">{certification?.certificate_number || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Institute:</span>
                      <span className="text-gray-900">{certification?.training_institute?.institute_name || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Training Date:</span>
                      <span className="text-gray-900">{certification?.training_date ? formatDate(certification.training_date) : '-'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Expiry Date:</span>
                      <span className={`${expired ? 'text-red-600 font-medium' : expiringSoon ? 'text-orange-600 font-medium' : 'text-gray-900'}`}>
                        {certification?.expiry_date ? formatDate(certification.expiry_date) : '-'}
                        {expired && <span className="ml-1 text-xs">(Expired)</span>}
                        {expiringSoon && <span className="ml-1 text-xs">(Expiring Soon)</span>}
                      </span>
                    </div>
                  </div>

                  {/* {(certification?.card_front_url || certification?.card_back_url) && (
                    <div className="mt-3 pt-3 border-t border-gray-200 flex gap-3">
                      {certification?.card_front_url && (
                        <a
                          href={certification.card_front_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                        >
                          <FiEye className="w-3 h-3" />
                          Front
                        </a>
                      )}
                      {certification?.card_back_url && (
                        <a
                          href={certification.card_back_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                        >
                          <FiEye className="w-3 h-3" />
                          Back
                        </a>
                      )}
                    </div>
                  )} */}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-800 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading user details...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FiAlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">User Not Found</h2>
          <p className="text-gray-600 mb-4">The user you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/users')}
            className="px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-700"
          >
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto">
        {/* Header */}
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">{getFullName(user)}</h1>
              <p className="text-sm text-gray-600 mt-1">{user.role.name}</p>
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mt-2">
                {/* <div className="flex items-center gap-1">
                  <FiMail size={12} />
                  {user.email}
                </div>
                <div className="flex items-center gap-1">
                  <FiPhone size={12} />
                  {formatPhoneNumber(user.country_code, user.phone_number)}
                </div> */}
                <div className="flex items-center gap-1">
                  <FiBriefcase size={12} />
                  {user.department?.name || 'N/A'}
                </div>
                <div className="flex items-center gap-1">
                  <FiMapPin size={12} />
                  {user.district?.name || 'N/A'}, {user.state?.state_name || 'N/A'}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {getStatusBadge(user.status)}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-800 text-blue-800'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' && renderDetailsTab()}
        {activeTab === 'contact' && renderContactTab()}
        {activeTab === 'projects' && renderProjectsTab()}
        {activeTab === 'certifications' && renderCertificationsTab()}
        {activeTab === 'devices' && renderDevicesTab()}
        {activeTab === 'logs' && renderLogsTab()}

        {/* User Details Offcanvas */}
        {showDetailsModal && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity z-50"
              onClick={() => !detailsSaving && setShowDetailsModal(false)}
            />
            <div className="fixed inset-y-0 right-0 z-50 w-full max-w-2xl bg-white shadow-xl transform transition-transform duration-300 ease-in-out flex flex-col">
              <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {userDetails ? 'Edit Extended Details' : 'Add Extended Details'}
                </h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  disabled={detailsSaving}
                  className="p-2 hover:bg-gray-100 rounded transition-colors"
                >
                  <FiX size={20} className="text-gray-600" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                  {/* Employment Information */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <FiBriefcase className="w-4 h-4" />
                      Employment Information
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Date of Joining</label>
                        <input
                          type="date"
                          value={detailsFormData.date_of_joining?.split('T')[0] || ''}
                          onChange={(e) => handleDetailsInputChange('date_of_joining', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      {/* <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Portal Access Status</label>
                        <select
                          value={detailsFormData.portal_access_status || ''}
                          onChange={(e) => handleDetailsInputChange('portal_access_status', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select Status</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          <option value="suspended">Suspended</option>
                        </select>
                      </div> */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Date of Leaving</label>
                        <input
                          type="date"
                          value={detailsFormData.date_of_leaving?.split('T')[0] || ''}
                          onChange={(e) => handleDetailsInputChange('date_of_leaving', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Company Email</label>
                        <input
                          type="email"
                          value={detailsFormData.email_company || ''}
                          onChange={(e) => handleDetailsInputChange('email_company', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="employee@company.com"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Referred By</label>
                        <input
                          type="text"
                          value={detailsFormData.referred_by || ''}
                          onChange={(e) => handleDetailsInputChange('referred_by', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Name of referrer"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">ESI Number</label>
                        <input
                          type="text"
                          value={detailsFormData.esi_number || ''}
                          onChange={(e) => handleDetailsInputChange('esi_number', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                          placeholder="1234567890"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">PF Number</label>
                        <input
                          type="text"
                          value={detailsFormData.pf_number || ''}
                          onChange={(e) => handleDetailsInputChange('pf_number', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                          placeholder="ABCDE1234567890123456"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Personal Information */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <FiUser className="w-4 h-4" />
                      Personal Information
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Alternate Mobile</label>
                        <input
                          type="tel"
                          value={detailsFormData.alternate_mobile_number || ''}
                          onChange={(e) => handleDetailsInputChange('alternate_mobile_number', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="9876543210"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Emergency Contact</label>
                        <input
                          type="tel"
                          value={detailsFormData.emergency_mobile_number || ''}
                          onChange={(e) => handleDetailsInputChange('emergency_mobile_number', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="9876543210"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Blood Group</label>
                        <select
                          value={detailsFormData.blood_group || ''}
                          onChange={(e) => handleDetailsInputChange('blood_group', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select Blood Group</option>
                          <option value="A+">A+</option>
                          <option value="A-">A-</option>
                          <option value="B+">B+</option>
                          <option value="B-">B-</option>
                          <option value="AB+">AB+</option>
                          <option value="AB-">AB-</option>
                          <option value="O+">O+</option>
                          <option value="O-">O-</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Marital Status</label>
                        <select
                          value={detailsFormData.marital_status || ''}
                          onChange={(e) => handleDetailsInputChange('marital_status', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select Status</option>
                          <option value="single">Single</option>
                          <option value="married">Married</option>
                          <option value="divorced">Divorced</option>
                          <option value="widowed">Widowed</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Education</label>
                        <input
                          type="text"
                          value={detailsFormData.education || ''}
                          onChange={(e) => handleDetailsInputChange('education', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="B.Tech in Computer Science"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Permanent Address</label>
                        <textarea
                          value={detailsFormData.address_permanent || ''}
                          onChange={(e) => handleDetailsInputChange('address_permanent', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter full address with pincode"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Identity Documents */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <FiCreditCard className="w-4 h-4" />
                      Identity Documents
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Aadhar Card Number</label>
                        <input
                          type="text"
                          value={detailsFormData.aadhar_card_number || ''}
                          onChange={(e) => handleDetailsInputChange('aadhar_card_number', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                          placeholder="123456789012"
                          maxLength={12}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">PAN Card Number</label>
                        <input
                          type="text"
                          value={detailsFormData.pan_card_number || ''}
                          onChange={(e) => handleDetailsInputChange('pan_card_number', e.target.value.toUpperCase())}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono uppercase"
                          placeholder="ABCDE1234F"
                          maxLength={10}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Driving Licence Number</label>
                        <input
                          type="text"
                          value={detailsFormData.licence_number || ''}
                          onChange={(e) => handleDetailsInputChange('licence_number', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                          placeholder="DL1234567890"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Bank & Statutory */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <FiCreditCard className="w-4 h-4" />
                      Bank & Statutory Information
                      {!canEditBankInfo && (
                        <span className="ml-2 text-xs text-amber-600 font-normal">(view only — no edit_bank permission)</span>
                      )}
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Bank Name</label>
                        <input
                          type="text"
                          value={detailsFormData.bank_name || ''}
                          onChange={(e) => handleDetailsInputChange('bank_name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                          placeholder="State Bank of India"
                          disabled={!canEditBankInfo}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Account Number</label>
                        <input
                          type="text"
                          value={detailsFormData.bank_account_number || ''}
                          onChange={(e) => handleDetailsInputChange('bank_account_number', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono disabled:bg-gray-50"
                          placeholder="12345678901234"
                          disabled={!canEditBankInfo}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">IFSC Code</label>
                        <input
                          type="text"
                          value={detailsFormData.ifsc_code || ''}
                          onChange={(e) => handleDetailsInputChange('ifsc_code', e.target.value.toUpperCase())}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono uppercase disabled:bg-gray-50"
                          placeholder="SBIN0001234"
                          maxLength={11}
                          disabled={!canEditBankInfo}
                        />
                      </div>
                      {(detailsFormData.payment_qr_code ? canEditQrCode : canCreateQrCode) && (
                        <div className="sm:col-span-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">Payment QR Code</label>
                          <DragDropFileInput
                            label="payment_qr_code"
                            onFileSelect={(file) => handleFileChange('payment_qr_code', file)}
                            selectedFile={detailsFiles['payment_qr_code'] || null}
                            existingFile={detailsFormData.payment_qr_code || null}
                            accept="image/*"
                          />
                          {detailsFormData.payment_qr_code && !detailsFiles['payment_qr_code'] && (
                            <div className="mt-2">
                              <img
                                src={detailsFormData.payment_qr_code}
                                alt="Current QR Code"
                                className="w-24 h-24 object-contain border border-gray-200 rounded"
                              />
                              <p className="text-xs text-gray-500 mt-1">Current QR code (upload new to replace)</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Family Information */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <FiUsers className="w-4 h-4" />
                      Family Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Father's Name</label>
                        <input
                          type="text"
                          value={detailsFormData.father_name || ''}
                          onChange={(e) => handleDetailsInputChange('father_name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Father's Aadhar</label>
                        <input
                          type="text"
                          value={detailsFormData.father_aadhar_card_number || ''}
                          onChange={(e) => handleDetailsInputChange('father_aadhar_card_number', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                          maxLength={12}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Mother's Name</label>
                        <input
                          type="text"
                          value={detailsFormData.mother_name || ''}
                          onChange={(e) => handleDetailsInputChange('mother_name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Mother's Aadhar</label>
                        <input
                          type="text"
                          value={detailsFormData.mother_aadhar_card_number || ''}
                          onChange={(e) => handleDetailsInputChange('mother_aadhar_card_number', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                          maxLength={12}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Spouse's Name</label>
                        <input
                          type="text"
                          value={detailsFormData.spouse_name || ''}
                          onChange={(e) => handleDetailsInputChange('spouse_name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Spouse's Aadhar</label>
                        <input
                          type="text"
                          value={detailsFormData.spouse_aadhar_card_number || ''}
                          onChange={(e) => handleDetailsInputChange('spouse_aadhar_card_number', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                          maxLength={12}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Child 1 Name</label>
                        <input
                          type="text"
                          value={detailsFormData.child_name_1 || ''}
                          onChange={(e) => handleDetailsInputChange('child_name_1', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Child 2 Name</label>
                        <input
                          type="text"
                          value={detailsFormData.child_name_2 || ''}
                          onChange={(e) => handleDetailsInputChange('child_name_2', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Document Uploads */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <FiUpload className="w-4 h-4" />
                      Document Uploads
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {[
                        { key: 'aadhar_card_document', label: 'Aadhar Card' },
                        { key: 'pan_card_document', label: 'PAN Card' },
                        { key: 'driving_licence_document', label: 'Driving Licence' },
                        { key: 'bank_passbook', label: 'Bank Passbook' },
                        { key: 'marksheet', label: 'Marksheet' },
                        { key: 'esi_tic_attachment', label: 'ESI TIC' },
                        { key: 'family_picture', label: 'Family Picture' },
                        { key: 'employee_id_card_temporary', label: 'Temp ID Card' },
                        { key: 'employee_id_card_permanent', label: 'Perm ID Card Front' },
                        { key: 'employee_id_card_permanent_back', label: 'Perm ID Card Back' },
                      ].map((doc) => (
                        <DragDropFileInput
                          key={doc.key}
                          label={doc.label}
                          onFileSelect={(file) => handleFileChange(doc.key, file)}
                          selectedFile={detailsFiles[doc.key] || null}
                          existingFile={userDetails?.[doc.key as keyof UserDetailsData] as string || null}
                          accept="image/*,.pdf"
                        />
                      ))}
                    </div>
                  </div>
                </div>

              <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-4 flex flex-col sm:flex-row gap-3 sm:justify-end">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  disabled={detailsSaving}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveUserDetails}
                  disabled={detailsSaving}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {detailsSaving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiSave className="w-4 h-4" />
                      Save Details
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        )}

        {/* Add Certification Modal */}
        {showAddCertificationModal && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity z-50"
              onClick={() => !addingCertification && setShowAddCertificationModal(false)}
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Link Certification</h3>
                  <button
                    onClick={() => setShowAddCertificationModal(false)}
                    disabled={addingCertification}
                    className="p-2 hover:bg-gray-100 rounded transition-colors"
                  >
                    <FiX size={20} className="text-gray-600" />
                  </button>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Certification
                  </label>
                  <SelectWithSearch2
                    placeholder="Search certifications..."
                    value={selectedCertification}
                    onChange={(option) => setSelectedCertification(option)}
                    apiEndpoint="/training/certifications/list"
                    dataKey={"training_certifications"}
                    mapOption={(cert: TrainingCertificationOption) => ({
                      id: cert.id,
                      value: cert.id,
                      label: `${cert.certificate_number} - ${cert.training_type?.training_name || 'Unknown'} (${formatDate(cert.training_date)})`,
                    })}
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    Search by certificate number or training type
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                  <button
                    onClick={() => {
                      setShowAddCertificationModal(false);
                      setSelectedCertification(null);
                    }}
                    disabled={addingCertification}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddCertification}
                    disabled={addingCertification || !selectedCertification}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {addingCertification ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Linking...
                      </>
                    ) : (
                      <>
                        <FiPlus className="w-4 h-4" />
                        Link Certification
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Logout Confirmation Modal */}
        {showLogoutConfirm && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity z-50"
              onClick={() => !logoutLoading && setShowLogoutConfirm(null)}
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Confirm Logout</h3>
                  <button
                    onClick={() => setShowLogoutConfirm(null)}
                    disabled={logoutLoading !== null}
                    className="p-2 hover:bg-gray-100 rounded transition-colors"
                  >
                    <FiX size={20} className="text-gray-600" />
                  </button>
                </div>

                <div className="mb-6">
                  <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <FiAlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium mb-1">Are you sure you want to logout this device?</p>
                      <p className="text-xs">This will immediately terminate the session on this device.</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                  <button
                    onClick={() => setShowLogoutConfirm(null)}
                    disabled={logoutLoading !== null}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleLogoutDevice(showLogoutConfirm)}
                    disabled={logoutLoading !== null}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {logoutLoading === showLogoutConfirm ? 'Logging out...' : 'Yes, Logout Device'}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UserView;
